/**
 * Races a promise against a timeout.
 * If the promise doesn't resolve within `ms` milliseconds, rejects with a
 * human-readable TimeoutError so the UI can show a Retry button instead of
 * spinning forever.
 *
 * Usage:
 *   const { data, error } = await withTimeout(supabase.from('firms').select('*'));
 *   const result          = await withTimeout(fetch('/api/something'), 5000);
 */
export class TimeoutError extends Error {
  constructor(ms) {
    super(`Request timed out after ${ms / 1000}s. Check your connection and try again.`);
    this.name = 'TimeoutError';
  }
}

export function withTimeout(promise, ms = 30000) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new TimeoutError(ms)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

/**
 * Retries a thunk (function returning a promise) up to `retries` times
 * with exponential backoff. Only retries on TimeoutError.
 *
 * Usage:
 *   const result = await withRetry(() => withTimeout(fetchFirm(firmId)), 2);
 */
export async function withRetry(fn, retries = 2, delayMs = 1000) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      const isRetryable = err instanceof TimeoutError || err?.message?.includes('timed out');
      if (!isRetryable || attempt === retries) throw err;
      await new Promise((r) => setTimeout(r, delayMs * Math.pow(2, attempt)));
    }
  }
  throw lastErr;
}
